import { ICoordinates, IGoogleMapState } from '../../../types'
import * as constants from '../../constants'
import { IGoogleMapsAction } from '../../actions/google_map'
import { IMarker, IMap } from '../../../types'
import { mapViewStrategyStages } from '../../../components/MapView'

const initState: IGoogleMapState = {
  origin: null,
  destination: null,
  additionalStop: [],
  center: null,
  markers: {},
  zoom: 15,
  polygon: null,
  zoneId: null,
  polyline: null,
  bounds: null,
  renderStrategyStage: mapViewStrategyStages.showInit
}

export function mapView(state: IGoogleMapState, action: IGoogleMapsAction): IGoogleMapState {
  if (typeof state === 'undefined') {
    return initState
  }
  switch (action.type) {
    case constants.SET_ORIGIN: {
      const origin = action.payload
      return Object.assign({}, state, {
        origin
      })
    }
    case constants.SET_DESTINATION: {
      return Object.assign({}, state, {
        destination: action.payload
      })
    }
    case constants.SET_ADDITIONAL_STOP: {
      const { additionalStop } = state
      return Object.assign({}, state, {
        additionalStop: [...additionalStop, action.payload]
      })
    }
    case constants.REMOVE_ADDITIONAL_STOP: {
      const { additionalStop } = state
      const filteredStops = additionalStop.filter(stop => stop.index !== action.payload)
      return Object.assign({}, state, {
        additionalStop: [...filteredStops]
      })
    }
    case constants.SET_CENTER:
      return Object.assign({}, state, {
        center: action.payload
      })
    case constants.SET_ZOOM:
      return Object.assign({}, state, {
        zoom: action.payload
      })
    case constants.SET_POLYGON:
      return Object.assign({}, state, {
        polygon: action.payload
      })
    case constants.ADD_MARKERS: {
      const { type, markers } = action.payload

      if (!markers || !markers.length) {
        return state
      }
      const allMarkers = { ...state.markers }
      const typeMarkers = allMarkers[type]
      allMarkers[type] = typeMarkers ? typeMarkers.concat(markers) : markers.slice(0)
      return { ...state, markers: allMarkers }
    }
    case constants.REPLACE_MARKERS: {
      const type = action.payload.type
      let { markers } = action.payload

      if (!markers || !markers.length) {
        markers = []
      }

      const allMarkers = { ...state.markers }
      if (type !== 'additionalStop') {
        allMarkers[type] = markers.slice(0)
        return { ...state, markers: allMarkers }
      } else {
        const { additionalStop } = allMarkers

        let selectedMarker
        selectedMarker = additionalStop ? additionalStop.find(stop => stop.index === markers[0].index) : undefined

        if (selectedMarker !== undefined) {
          const filteredStops = additionalStop.filter(stop => stop.index !== markers[0].index)
          const newAdditionalStopMarkers = [...filteredStops, markers[0]]

          return {
            ...state,
            markers: {
              ...allMarkers,
              additionalStop: newAdditionalStopMarkers.sort((a, b) => (a.index as number) - (b.index as number))
            }
          }
        } else {
          const newAdditionalStopMarkers = additionalStop ? [...additionalStop, markers[0]] : markers

          return {
            ...state,
            markers: {
              ...allMarkers,
              additionalStop: newAdditionalStopMarkers.sort((a, b) => (a.index as number) - (b.index as number))
            }
          }
        }
      }
    }

    case constants.REMOVE_MARKER: {
      const index = action.payload
      const allMarkers = { ...state.markers }
      const { additionalStop } = allMarkers

      let filteredStops: IMarker[] = []

      if (additionalStop !== undefined) {
        filteredStops = additionalStop.filter(stop => (stop.index as number) !== index)

        return {
          ...state,
          markers: {
            ...allMarkers,
            additionalStop: [...filteredStops].sort((a, b) => (a.index as number) - (b.index as number))
          }
        }
      } else {
        return {
          ...state,
          markers: {
            ...allMarkers,
            additionalStop: []
          }
        }
      }
    }

    case constants.CLEAR_MARKERS: {
      const { type } = action.payload
      if (!type) {
        return { ...state, markers: {} }
      }
      const newMarkers = { ...state.markers }
      delete newMarkers[type]

      return { ...state, markers: newMarkers }
    }
    case constants.SET_POLYLINE:
      return Object.assign({}, state, {
        polyline: action.payload
      })
    case constants.SET_MAP_ZONE: {
      return Object.assign({}, state, {
        zoneId: action.payload
      })
    }
    case constants.SET_BOUNDS: {
      return Object.assign({}, state, {
        bounds: action.payload
      })
    }
    case constants.SET_INIT_CENTER: {
      return Object.assign({}, state, { ...initState })
    }
    case constants.SET_OPERATOR_LOCATION:
      return Object.assign({}, state, {
        operatorLocation: action.payload
      })
    case constants.SET_MAP_VIEW_STRATEGY_STAGE:
      return Object.assign({}, state, {
        renderStrategyStage: action.payload
      })
    case constants.UPDATE_ADDITIONAL_STOP_SEQUENCE: {
      const { fromIndex, toIndex } = action.payload
      const { additionalStop, markers } = state
      const newAdditionalStopSequence = additionalStop.filter(location => additionalStop[fromIndex] !== location)
      newAdditionalStopSequence.splice(toIndex, 0, additionalStop[fromIndex])
      const newMarkerSequence = newAdditionalStopSequence.map(({ index, lat, lng }) => ({
        index,
        lat,
        lng,
        type: 'additionalStop'
      }))
      return Object.assign({}, state, {
        additionalStop: newAdditionalStopSequence,
        markers: {
          ...markers,
          additionalStop: newMarkerSequence
        }
      })
    }
    case constants.UPDATE_LOCATION_SEQUENCE: {
      const { fromIndex, toIndex } = action.payload
      const { origin, destination, additionalStop } = state
      const locationsArr = [origin, ...additionalStop, destination]
      const ORIGIN_INDEX = -1
      const DESTINATION_INDEX = -2

      const parseIndex = (index: number) => {
        return index === ORIGIN_INDEX ? 0 : index === DESTINATION_INDEX ? locationsArr.length - 1 : index + 1
      }
      const parseMarkerDetails = (location: ICoordinates[], type: string) => {
        const marker = location.map(({ lat, lng }) => ({
          type,
          lat,
          lng
        }))
        return marker
      }

      const fromLocIndex = parseIndex(fromIndex)
      const toLocIndex = parseIndex(toIndex)

      const newLocationSequence = locationsArr.filter(location => locationsArr[fromLocIndex] !== location)
      newLocationSequence.splice(toLocIndex, 0, locationsArr[fromLocIndex])

      const newAdditionalStop = newLocationSequence.filter(
        (loc, index): loc is ICoordinates => index !== 0 && index !== newLocationSequence.length - 1 && loc !== null
      )
      const newDestination = newLocationSequence[newLocationSequence.length - 1]
      const newOrigin = newLocationSequence[0]
      const newMarkers = {
        additionalStop: newAdditionalStop ? parseMarkerDetails(newAdditionalStop, 'additionalStop') : [],
        destination: newDestination ? parseMarkerDetails([newDestination], 'destination') : [],
        origin: newOrigin ? parseMarkerDetails([newOrigin], 'origin') : []
      }

      return Object.assign({}, state, {
        origin: newOrigin,
        additionalStop: newAdditionalStop,
        destination: newDestination,
        markers: newMarkers
      })
    }
    default:
      return state
  }
}



// WEBPACK FOOTER //
// ./src/state/reducers/google_map/index.ts